import { useCallback, useEffect, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { CallRouteType, PhoneNumber } from '@weave/schema-gen-ts/dist/schemas/phone/callroute/v1/callroute_service.pb';
import { CallRouteV1 } from '@frontend/api-call-route';
import { scopedFilterAndSort } from '@frontend/filters';
import { useTranslation } from '@frontend/i18n';
import { usePhoneLocationFilterShallowStore, usePhoneSettingsShallowStore } from '@frontend/phone-config';
import { formatPhoneNumber } from '@frontend/phone-numbers';
import { useAppScopeStore } from '@frontend/scope';
import { Heading, Modal, useModalControl, Table, Chip, Text } from '@frontend/design-system';
import { logError } from '../../helpers/utils';
import { NumbersPanelTabs } from '../../numbers-panel/call-route-tabs';
import { RoutesListTable } from '../../routes-list-table/routes-list-table';
import { RoutesTableTab, RoutesTableTabType, RoutesTabs } from '../../routes-list-table/routes-tabs';
import { ExtendedCallRoute } from '../../types';
import { Step } from './atoms/step';
import { AddStepPanelProps } from './types';

export const CallRouteStep = ({
  initialState,
  callObjectsData,
  onProceedClick,
}: AddStepPanelProps<
  CallRouteV1.Types.NodeType.NODE_TYPE_CALL_ROUTE | CallRouteV1.Types.NodeType.NODE_TYPE_DEVICE_EXTENSION
>) => {
  const { t } = useTranslation('phone');

  const { locationFilters, setLocationFilters } = usePhoneLocationFilterShallowStore(
    'locationFilters',
    'setLocationFilters'
  );
  const filteredLocations = locationFilters.callRoutesIndex;

  const { settingsTenantLocation } = usePhoneSettingsShallowStore('settingsTenantLocation');
  const { getScopeName } = useAppScopeStore();
  const {
    modalProps: numbersModalProps,
    closeModal: closeNumbersModal,
    openModal: openNumbersModal,
  } = useModalControl();
  const {
    modalProps: extensionsModalProps,
    closeModal: closeExtensionsModal,
    openModal: openExtensionsModal,
  } = useModalControl();
  const [selectedCallRoute, setSelectedCallRoute] = useState<ExtendedCallRoute | undefined>(undefined);
  // The call route that is currently being viewed in one of the modals.
  const [viewingCallRoute, setViewingCallRoute] = useState<ExtendedCallRoute | undefined>(undefined);
  const [selectedTab, setSelectedTab] = useState<RoutesTableTabType>(
    initialState?.type === CallRouteV1.Types.NodeType.NODE_TYPE_DEVICE_EXTENSION
      ? RoutesTableTab.DEVICE_ROUTES
      : RoutesTableTab.CALL_ROUTES
  );

  useEffect(() => {
    if (!numbersModalProps.show && !extensionsModalProps.show) {
      setViewingCallRoute(undefined);
    }
  }, [numbersModalProps.show, extensionsModalProps.show]);

  const { routes, routesIsLoading, allPhoneNumbers, allPhoneNumbersIsLoading } = callObjectsData;

  const callRouteLocationIds = useMemo(() => routes.map((callRoute) => callRoute.locationId).filter(Boolean), [routes]);

  const filteredCallRoutes = useMemo(
    () =>
      scopedFilterAndSort({
        data: routes,
        scopeIdAccessor: (callRoute) => (callRoute.locationId ? [callRoute.locationId] : []),
        scopeIds: callRouteLocationIds,
        filteredScopeIds: filteredLocations,
      }),
    [callRouteLocationIds, routes, filteredLocations]
  );

  const isLoading = routesIsLoading || allPhoneNumbersIsLoading;

  // tableData is the callRoutes data with the phoneNumbers mapped to the callRoute (since
  // the callRoute only has an array of phoneNumberIds). The
  // phoneOverrides data has the callRouteId that maps to a callRoute.
  const tableData = useMemo(() => {
    const extendedData =
      filteredCallRoutes?.map((callRoute) => {
        const phoneNumberIds = callRoute.phoneNumberIds ?? [];
        const phoneNumbersForCallRoute =
          allPhoneNumbers?.filter((phoneNumber) => phoneNumberIds.includes(phoneNumber.id)) ?? [];
        const phoneNumbers: PhoneNumber[] = phoneNumbersForCallRoute.map((phoneNumber) => ({
          id: phoneNumber.id,
          phoneNumber: String(phoneNumber.phoneNumber?.nationalNumber ?? ''),
          locationId: phoneNumber.locationId ?? '',
          displayName: phoneNumber.displayName ?? '',
        }));

        return {
          ...callRoute,
          phoneNumbers,
          callRoutePhoneOverride: undefined,
        };
      }) ?? [];

    return extendedData;
  }, [filteredCallRoutes, allPhoneNumbers]);

  useEffect(() => {
    if (initialState?.callObject?.primitiveId && !selectedCallRoute) {
      setSelectedCallRoute(
        tableData.find((callRoute) => callRoute.callRouteId === initialState?.callObject?.primitiveId)
      );
    }
  }, [initialState, selectedCallRoute, tableData]);

  const handleProceedClick = useCallback(() => {
    if (!selectedCallRoute) {
      logError('No call route selected when proceeding with call route step', 'CallRouteStep:handleProceedClick', {
        initialState,
      });
      return;
    }

    const callRoutePhoneNumbers =
      allPhoneNumbers
        ?.filter((phoneNumber) => {
          const belongsToCallRoute = selectedCallRoute.phoneNumberIds.includes(phoneNumber.id);
          return belongsToCallRoute && !!phoneNumber.phoneNumber?.nationalNumber;
        })
        ?.map((item) => String(item.phoneNumber?.nationalNumber ?? '')) ?? [];

    onProceedClick({
      type:
        selectedCallRoute.callRouteType === CallRouteType.CALL_ROUTE_TYPE_EXTENSION
          ? CallRouteV1.Types.NodeType.NODE_TYPE_DEVICE_EXTENSION
          : CallRouteV1.Types.NodeType.NODE_TYPE_CALL_ROUTE,
      callObject: {
        primitiveId: selectedCallRoute.callRouteId,
        primitiveName: selectedCallRoute.name,
        instructionId: initialState?.callObject?.instructionId ?? '',
        instructionSetId: initialState?.callObject?.instructionSetId ?? '',
        callRouteExpansion: {
          extensions: selectedCallRoute.extensionNumbers.map((ext) => String(ext)),
          phoneNumbers: callRoutePhoneNumbers,
          locationId: selectedCallRoute.locationId,
        },
      },
    });
  }, [selectedCallRoute, onProceedClick, initialState, allPhoneNumbers]);

  const moreTextClickHandler = (type: NumbersPanelTabs, callRoute?: ExtendedCallRoute) => {
    setViewingCallRoute(callRoute);
    if (type === NumbersPanelTabs.PHONE_NUMBERS) {
      openNumbersModal();
    } else if (type === NumbersPanelTabs.EXTENSIONS) {
      openExtensionsModal();
    }
  };

  const hasChildLocations = Boolean(settingsTenantLocation?.children?.length);

  return (
    <>
      <Step
        title={t('Call Route')}
        primaryButtonDisabled={!selectedCallRoute}
        onPrimaryButtonClick={handleProceedClick}
      >
        <div
          css={css`
            display: flex;
            justify-content: space-between;
          `}
        >
          <Heading
            css={css`
              display: inline-block;
            `}
            level={3}
          >
            {t('Select Call Route')}
          </Heading>
        </div>

        {settingsTenantLocation ? (
          <>
            <RoutesTabs
              selectedTab={selectedTab}
              setSelectedTab={setSelectedTab}
              trackingIdPrefix='call-routes-add-step-'
            />
            <RoutesListTable
              tableData={tableData}
              filteredLocations={filteredLocations}
              setLocationFilters={setLocationFilters}
              isLoading={isLoading}
              settingsTenantLocation={settingsTenantLocation}
              clickableCallRoutes={false}
              initialSelectedCallRouteId={initialState?.callObject?.primitiveId}
              onMoreTextClick={moreTextClickHandler}
              onRowSelect={setSelectedCallRoute}
              selectedTab={selectedTab}
              fullHeight={false}
            />
          </>
        ) : (
          <Text>{t('Please select a location to view Call Routes.')}</Text>
        )}
      </Step>

      <Modal {...numbersModalProps} minWidth={hasChildLocations ? 600 : 400}>
        <Modal.Header onClose={closeNumbersModal}>{t('Phone Numbers')}</Modal.Header>
        <Modal.Label>{viewingCallRoute?.name}</Modal.Label>
        <Modal.Body>
          <Table
            data={viewingCallRoute?.phoneNumbers ?? []}
            colConfig={[
              {
                Header: t('Phone Number'),
                accessor: (phoneNumber) => phoneNumber.phoneNumber,
                id: 'phone-number',
                cellRenderer: (phoneNumber) => formatPhoneNumber(phoneNumber),
                width: 180,
              },
              {
                Header: t('Location'),
                accessor: (phoneNumber) => phoneNumber.locationId,
                cellRenderer: (locationId) =>
                  locationId ? <Chip.SingleChip>{getScopeName(locationId)}</Chip.SingleChip> : '',
                id: 'location-name',
                sortType: 'caseInsensitive',
                width: 190,
                omit: !hasChildLocations,
              },
            ]}
          />
        </Modal.Body>
        <Modal.Footer
          secondary={{
            label: t('Close'),
            onClick: closeNumbersModal,
          }}
        />
      </Modal>

      <Modal {...extensionsModalProps} minWidth={400}>
        <Modal.Header onClose={closeExtensionsModal}>{t('Extensions')}</Modal.Header>
        <Modal.Label>{viewingCallRoute?.name}</Modal.Label>
        <Modal.Body>
          <Table
            data={viewingCallRoute?.extensionNumbers.map((extension) => ({ extension })) ?? []}
            colConfig={[
              {
                Header: t('Extension'),
                accessor: (row) => row.extension,
                id: 'extension',
              },
            ]}
          />
        </Modal.Body>
        <Modal.Footer
          secondary={{
            label: t('Close'),
            onClick: closeExtensionsModal,
          }}
        />
      </Modal>
    </>
  );
};
